import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";

const intervalMS = 5 * 60 * 1000;

function registerPeriodicSync(swUrl: string, r: ServiceWorkerRegistration) {
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return setInterval(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (r.installing || !navigator) return;

    if ("onLine" in navigator && !navigator.onLine) return;

    try {
      const resp = await fetch(swUrl, {
        cache: "no-store",
        headers: {
          cache: "no-store",
          "cache-control": "no-cache",
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (resp?.status === 200) await r.update();
    } catch {
      // Network failures are expected (e.g. right after iOS resumes a
      // backgrounded tab); the next tick will retry.
    }
  }, intervalMS);
}

export function AppUpdater() {
  const { t } = useTranslation();

  let interval: ReturnType<typeof setInterval> | undefined;

  const { updateServiceWorker } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      r?.addEventListener("updatefound", () => {
        clearInterval(interval);
      });
      if (r?.active?.state === "activated") {
        // eslint-disable-next-line react-hooks/immutability
        interval = registerPeriodicSync(swUrl, r);
      } else if (r?.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") {
            interval = registerPeriodicSync(swUrl, r);
          }
        });
      }
    },
    onNeedRefresh() {
      toast(t("app_updater.title"), {
        id: "app-update",
        action: {
          label: t("app_updater.reload"),
          onClick: () => void updateServiceWorker(true),
        },
        duration: Infinity,
      });
    },
  });

  return null;
}
